import { createFileRoute, Link } from "@tanstack/react-router";
import { trackConversion } from "@/lib/gtag";
import { Phone, Mail, MessageCircle, Clock, MapPin, Car } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { IdleCallNudge } from "@/components/IdleCallNudge";

const URL = "https://sepremiumcabs.com/contact";
const TITLE = "Contact SE Premium Cabs | Book a Melbourne Taxi 24/7";
const DESC =
  "Contact SE Premium Cabs for a Melbourne taxi or airport transfer. Call 0493 100 265, WhatsApp or book online — 24/7, seven days, across Melbourne.";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: TITLE,
          description: DESC,
          url: URL,
          mainEntity: {
            "@type": "TaxiService",
            name: "SE Premium Cabs",
            telephone: "+61493100265",
            email: "sepremiumcabs@gmail.com",
            url: "https://sepremiumcabs.com/",
            areaServed: "Melbourne, Victoria, Australia",
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "00:00",
              closes: "23:59",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sepremiumcabs.com/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: URL },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <div className="mx-auto max-w-4xl px-6 py-12 pb-28 lg:py-20 lg:pb-20">
        <nav className="mb-8 text-xs font-semibold tracking-widest text-muted-foreground">
          <Link to="/" className="hover:text-primary">HOME</Link>
          <span className="mx-2">/</span>
          <span className="text-primary">CONTACT</span>
        </nav>

        <h1 className="font-serif text-4xl font-bold tracking-tight lg:text-5xl">
          Contact <span className="text-primary">SE Premium Cabs</span>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-muted-foreground lg:text-lg">
          The fastest way to get a cab is to phone us — we answer 24 hours a day, seven days a
          week, including public holidays. You can also send a WhatsApp message or submit the
          online booking form and we will confirm the details with you.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="flex items-center gap-3 rounded-lg bg-primary px-6 py-5 text-sm font-bold tracking-wider text-primary-foreground"
          >
            <Phone className="h-5 w-5" /> CALL 0493 100 265
          </a>
          <a
            href="https://wa.me/61493100265?text=Hi%20SE%20Premium%20Cabs%2C%20I%27d%20like%20to%20book%20a%20ride"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackConversion("whatsapp")}
            className="flex items-center gap-3 rounded-lg border border-primary px-6 py-5 text-sm font-bold tracking-wider text-primary"
          >
            <MessageCircle className="h-5 w-5" /> MESSAGE ON WHATSAPP
          </a>
          <a
            href="mailto:sepremiumcabs@gmail.com"
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-card px-6 py-5 text-sm font-semibold"
          >
            <Mail className="h-5 w-5 text-primary" /> sepremiumcabs@gmail.com
          </a>
          <Link
            to="/"
            hash="book"
            className="flex items-center gap-3 rounded-lg border border-border/60 bg-card px-6 py-5 text-sm font-semibold"
          >
            <Car className="h-5 w-5 text-primary" /> Book online in 30 seconds
          </Link>
        </div>

        <section className="mt-14 grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border/60 bg-card p-6">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-primary" />
              <h2 className="text-sm font-bold tracking-wider">Hours</h2>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Open 24 hours, 7 days a week, including public holidays. Airport pickups are best
              booked at least 2 hours ahead; long-distance trips a day ahead.
            </p>
          </div>
          <div className="rounded-lg border border-border/60 bg-card p-6">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-primary" />
              <h2 className="text-sm font-bold tracking-wider">Service area</h2>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              We are a service-area business operating across Melbourne, Victoria — the south
              east and eastern suburbs, the CBD and Melbourne Airport (Tullamarine). We come to
              you; there is no shopfront to visit. See the{" "}
              <Link to="/suburbs" className="text-primary">suburbs we serve</Link>.
            </p>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-serif text-2xl font-bold tracking-tight">What to have ready</h2>
          <div className="mt-2 h-px w-16 bg-primary" />
          <ul className="mt-6 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li>Pickup address and destination</li>
            <li>Date and time (and flight number for airport pickups)</li>
            <li>Number of passengers and bags, so we send the right vehicle</li>
            <li>Any child seat or accessibility requirement</li>
          </ul>
        </section>

        <section className="mt-14 rounded-xl border border-primary/40 bg-card p-8 text-center">
          <h2 className="font-serif text-2xl font-bold tracking-tight">Need a cab now?</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Phone bookings are answered around the clock.
          </p>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground"
          >
            <Phone className="h-4 w-4" /> CALL 0493 100 265
          </a>
        </section>
      </div>
      <SiteFooter />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}
